import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Loader2, Lock, ArrowRight, Shield } from "lucide-react";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const WIX_SUBSCRIBE =
  process.env.REACT_APP_WIX_SUBSCRIPTION_URL ||
  "https://www.auditiq.ai/subscribe";

const CERT_COLORS = {
  Gold:   { bg: "#c9a84c", fg: "#0d1f3c" },
  Silver: { bg: "#b8b8b8", fg: "#0d1f3c" },
  Bronze: { bg: "#cd7f32", fg: "#ffffff" },
  Watch:  { bg: "#8e44ad", fg: "#ffffff" },
};

const PillarBar = ({ label, score }) => {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const color = pct >= 70 ? "#5bbf85" : pct >= 50 ? "#c9a84c" : "#e85b5b";
  return (
    <div className="space-y-1.5" data-testid={`demo-pillar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex justify-between text-[11px] uppercase tracking-[0.18em]">
        <span className="text-cream-dim">{label}</span>
        <span className="text-gold font-mono">{pct}</span>
      </div>
      <div className="h-2 rounded-full bg-navy-deep overflow-hidden border border-gold-dim/20">
        <div className="h-full rounded-full transition-all"
             style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

const DemoPreview = () => {
  const { scenarioKey } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!scenarioKey) return;
    axios.post(`${API}/auditiq/demo/crawler-scenario/${scenarioKey}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Could not load demo scenario"));
  }, [scenarioKey]);

  if (error) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric mb-3">Demo preview</div>
          <h1 className="font-display text-3xl text-cream">{error}</h1>
          <Link to="/" className="inline-block mt-6">
            <Button className="btn-ghost-gold h-11 px-5">Return home</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 flex items-center gap-3 text-cream-dim">
          <Loader2 size={16} className="animate-spin" /> Loading demo scenario…
        </div>
      </Layout>
    );
  }

  const cert = data.certification_level || "Watch";
  const certStyle = CERT_COLORS[cert] || CERT_COLORS.Watch;
  const pillars = Object.entries(data.pillar_scores || {});

  return (
    <Layout>
      <SEOHead title={`${data.label} | Demo Preview | AuditIQ`} path={`/portal/demo/${scenarioKey}`} />

      {/* Header banner */}
      <section className="border-b border-gold-dim/20 bg-navy-deep/60">
        <div className="container-wide py-10">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-electric mb-3">
            <Shield size={12} /> Demo Preview · Simulated Organization
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-cream">{data.label}</h1>
          <div className="text-sm text-cream-dim mt-2">{data.org_name}</div>
        </div>
      </section>

      {/* Unblurred KPI + Pillar + Chet priority */}
      <section className="container-wide py-10" data-testid="demo-preview-top">
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="glass p-5" data-testid="demo-kpi-score">
            <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Overall Score</div>
            <div className="metric text-4xl text-gold mt-1">{data.overall_score}</div>
            <div className="text-xs text-cream-dim mt-1">of 100</div>
          </div>
          <div className="glass p-5" data-testid="demo-kpi-cert">
            <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Certification</div>
            <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-bold"
                 style={{ background: certStyle.bg, color: certStyle.fg }}>
              {cert}
            </div>
          </div>
          <div className="glass p-5" data-testid="demo-kpi-risk">
            <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Risk Score</div>
            <div className="metric text-4xl text-gold mt-1">{data.risk_score}</div>
            <div className="text-xs text-cream-dim mt-1">higher = more risk</div>
          </div>
          <div className="glass p-5" data-testid="demo-kpi-findings">
            <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Findings</div>
            <div className="metric text-4xl text-gold mt-1">{data.findings_count}</div>
            <div className="text-xs text-cream-dim mt-1">across all modules</div>
          </div>
        </div>

        {/* Chet priority + exec summary */}
        <div className="glass p-6 mb-8" data-testid="demo-chet-summary">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Chet Executive Intelligence</div>
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-[color:var(--auditiq-critical)]/15 text-[color:var(--auditiq-critical)] border border-[color:var(--auditiq-critical)]/40">
              {data.chet_priority_level} Priority
            </span>
          </div>
          <p className="text-cream-dim mt-3 leading-relaxed">{data.executive_summary}</p>
        </div>

        {/* Five Pillars */}
        <div className="glass p-6">
          <div className="text-[10px] uppercase tracking-[0.26em] text-electric mb-4">Five Pillars of Transparency</div>
          <div className="grid md:grid-cols-2 gap-5">
            {pillars.map(([label, score]) => (
              <PillarBar key={label} label={label} score={score} />
            ))}
          </div>
        </div>
      </section>

      {/* Blurred bottom section + CTA overlay */}
      <section className="relative" data-testid="demo-preview-blur-overlay">
        <div className="container-wide py-10" style={{ filter: "blur(12px)", userSelect: "none", pointerEvents: "none" }}>
          <div className="glass p-6 mb-6">
            <div className="text-sm text-cream-dim">Top Risks · Recommended Actions · Savings Forecast · Hawkins 3+3+1 · Standards Compliance · All Findings Table · CSV/PDF Export …</div>
            <div className="h-64 mt-4 bg-navy-deep/40 rounded" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass p-6"><div className="h-40 bg-navy-deep/40 rounded" /></div>
            <div className="glass p-6"><div className="h-40 bg-navy-deep/40 rounded" /></div>
          </div>
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="glass p-8 max-w-md text-center pointer-events-auto" data-testid="demo-preview-cta">
            <Lock size={28} className="text-gold mx-auto" />
            <h3 className="font-display text-2xl text-cream mt-4">See the full audit results</h3>
            <p className="text-sm text-cream-dim mt-2">
              Join AuditIQ to unlock the full dashboard — all findings,
              recommended actions, savings forecast, and exportable reports.
            </p>
            <a href={WIX_SUBSCRIBE} target="_blank" rel="noopener noreferrer" data-testid="demo-preview-subscribe-btn">
              <Button className="btn-gold h-12 px-6 mt-5">
                Join AuditIQ <ArrowRight size={16} className="ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DemoPreview;
